import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { toast } from "sonner";
import { fromNow } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, Plus, MessageSquare, Trash2, Sparkles, Bot } from "lucide-react";
import { LeadReferenceRenderer } from "@/components/LeadReferenceRenderer";

/**
 * Shared AI Manager chat component. Used by:
 *   - Admin `/admin/ai` Chat tab (compact=false, side sessions rail visible)
 *   - Agent floating widget (compact=true, hides the rail, minimal chrome)
 *
 * Handles: sessions CRUD, SSE streaming, optimistic bubbles, lead-token
 * rendering. Underlying endpoints (/ai/sessions/*) are already CRM-aware once
 * the server prepends CRM context to the system prompt.
 */
export function AIManagerChat({ compact = false, role = "agent", managerName = "AI Manager" }) {
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const scrollerRef = useRef(null);
  const abortRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/sessions");
      setSessions(data || []);
      if (!activeId && data && data.length) setActiveId(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingList(false); }
  }, [activeId]);

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    setLoadingMsgs(true);
    api.get(`/ai/sessions/${activeId}/messages`)
      .then((r) => setMessages(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingMsgs(false));
    return () => { if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; } };
  }, [activeId]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const newSession = async () => {
    try {
      const { data } = await api.post("/ai/sessions", { title: "New chat" });
      setSessions((s) => [data, ...s]);
      setActiveId(data.id);
      setMessages([]);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteSession = async (id) => {
    if (!window.confirm("Delete this chat?")) return;
    try {
      await api.delete(`/ai/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.id !== id));
      if (activeId === id) { setActiveId(null); setMessages([]); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    let sid = activeId;
    if (!sid) {
      try {
        const { data } = await api.post("/ai/sessions", { title: content.slice(0, 60) });
        setSessions((s) => [data, ...s]);
        sid = data.id;
        setActiveId(sid);
      } catch (e) { toast.error(formatApiError(e)); return; }
    }
    setInput("");
    setSending(true);
    const tmpUserId = `tmp-u-${Date.now()}`;
    const tmpAssistantId = `tmp-a-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { id: tmpUserId, role: "user", content, created_at: new Date().toISOString() },
      { id: tmpAssistantId, role: "assistant", content: "", created_at: new Date().toISOString(), streaming: true },
    ]);

    const token = tokenStore.get();
    const ctl = new AbortController();
    abortRef.current = ctl;
    try {
      const res = await fetch(`${API_BASE}/ai/sessions/${sid}/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ content }),
        signal: ctl.signal,
      });
      if (!res.ok || !res.body) throw new Error((await res.text().catch(() => "")) || `Stream failed (${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuf = "";
      let assistantAccum = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        sseBuf += decoder.decode(value, { stream: true });
        // Parse SSE events
        let idx;
        while ((idx = sseBuf.indexOf("\n\n")) !== -1) {
          const raw = sseBuf.slice(0, idx);
          sseBuf = sseBuf.slice(idx + 2);
          const lines = raw.split("\n");
          let event = "message"; let data = "";
          for (const l of lines) {
            if (l.startsWith("event: ")) event = l.slice(7).trim();
            else if (l.startsWith("data: ")) data += l.slice(6);
          }
          if (!data) continue;
          let payload; try { payload = JSON.parse(data); } catch { continue; }
          if (event === "delta" && payload.delta) {
            assistantAccum += payload.delta;
            setMessages((m) => m.map((x) => (x.id === tmpAssistantId ? { ...x, content: assistantAccum } : x)));
          } else if (event === "user" && payload.id) {
            setMessages((m) => m.map((x) => (x.id === tmpUserId ? { ...x, id: payload.id } : x)));
          } else if (event === "done" && payload.assistant) {
            setMessages((m) => m.map((x) => (x.id === tmpAssistantId ? { ...payload.assistant, streaming: false } : x)));
            // Refresh session list for title update
            loadSessions();
          } else if (event === "error") {
            throw new Error(payload.error || "AI error");
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(e.message);
        setMessages((m) => m.filter((x) => x.id !== tmpAssistantId && x.id !== tmpUserId));
      }
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const suggestions = useMemo(() => (
    role === "admin"
      ? [
          "Give me today's team summary",
          "Which agents have overdue follow-ups?",
          "Show me stale hot leads",
          "Draft a nudge for agents with no updates today",
        ]
      : [
          "What should I focus on today?",
          "Show my overdue follow-ups",
          "Which of my hot leads need attention?",
          "Summarise my pipeline",
        ]
  ), [role]);

  return (
    <div className={`flex ${compact ? "h-full" : "h-[calc(100vh-8rem)] min-h-[420px] rounded-xl border border-border bg-card"} w-full overflow-hidden`}>
      {!compact && (
        <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-background/60 md:flex">
          <div className="border-b border-border p-3">
            <Button size="sm" onClick={newSession} className="w-full rounded-full" data-testid="ai-new-session-btn">
              <Plus className="mr-1 h-4 w-4" /> New chat
            </Button>
          </div>
          <div className="flex-1 space-y-1 overflow-y-auto p-2">
            {loadingList ? (
              <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            ) : sessions.length === 0 ? (
              <div className="px-2 py-6 text-center text-xs text-muted-foreground">No chats yet.</div>
            ) : sessions.map((s) => (
              <div key={s.id}
                   className={`group flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-xs ${activeId === s.id ? "bg-primary/10 text-foreground" : "hover:bg-accent text-muted-foreground"}`}
                   onClick={() => setActiveId(s.id)}
                   data-testid={`ai-session-${s.id}`}>
                <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                <div className="min-w-0 flex-1 truncate">{s.title}</div>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                        className="opacity-0 group-hover:opacity-100 text-destructive"
                        data-testid={`ai-session-delete-${s.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </aside>
      )}

      <main className="flex min-w-0 flex-1 flex-col">
        <div ref={scrollerRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="ai-messages-scroll">
          {loadingMsgs ? (
            <div className="flex justify-center py-10"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : messages.length === 0 ? (
            <EmptyIntro name={managerName} suggestions={suggestions} onPick={(s) => send(s)} />
          ) : (
            messages.map((m) => (
              <MessageBubble key={m.id} m={m} nav={nav} managerName={managerName} />
            ))
          )}
        </div>

        <div className="border-t border-border bg-background/70 p-3">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Ask ${managerName}…`}
              rows={compact ? 1 : 2}
              className="min-h-[42px] resize-none"
              data-testid="ai-input"
              disabled={sending}
            />
            <Button type="submit" disabled={sending || !input.trim()} className="rounded-full" data-testid="ai-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
          <div className="mono mt-1 text-[10px] text-muted-foreground">
            {managerName} · Enter to send, Shift+Enter for newline
          </div>
        </div>
      </main>
    </div>
  );
}

function EmptyIntro({ name, suggestions, onPick }) {
  const [brief, setBrief] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.get("/ai/manager/monitoring")
      .then((r) => { if (alive) setBrief(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const c = brief?.counts || {};
  const hasWork = c.overdue_followups || c.hot_no_followup || c.stale || c.needs_update;
  return (
    <div className="mx-auto max-w-md animate-fade-up space-y-4">
      <div className="rounded-xl p-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Bot className="h-6 w-6" />
        </div>
        <div className="mono mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">Northline AI</div>
        <h2 className="mt-1 font-display text-2xl font-bold tracking-tight">Hi, I'm {name}.</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ask about a lead, get message ideas, or plan your day.
        </p>
      </div>
      {brief && hasWork ? (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm" data-testid="ai-daily-brief">
          <div className="mono mb-1 text-[10px] uppercase tracking-widest text-primary">Today's brief</div>
          <ul className="space-y-1">
            {c.overdue_followups > 0 && <li>🔴 <b>{c.overdue_followups}</b> overdue follow-up{c.overdue_followups === 1 ? "" : "s"}</li>}
            {c.hot_no_followup > 0 && <li>🔥 <b>{c.hot_no_followup}</b> hot lead{c.hot_no_followup === 1 ? "" : "s"} without a follow-up</li>}
            {c.stale > 0 && <li>⚪ <b>{c.stale}</b> stale lead{c.stale === 1 ? "" : "s"} (3d+ silent)</li>}
            {c.needs_update > 0 && <li>📝 <b>{c.needs_update}</b> lead{c.needs_update === 1 ? "" : "s"} missing notes</li>}
          </ul>
          <button
            data-testid="ai-daily-brief-cta"
            onClick={() => onPick("Give me today's action plan for these leads")}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            Ask {name} what to do first →
          </button>
        </div>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        {suggestions.map((s, i) => (
          <button key={s} data-testid={`ai-suggestion-${i}`} onClick={() => onPick(s)}
                  className="rounded-lg border border-border bg-background p-3 text-left text-sm text-muted-foreground tap-lift hover:border-primary/50 hover:bg-accent hover:text-foreground">
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ m, nav, managerName }) {
  const isUser = m.role === "user";
  return (
    <div data-testid={`ai-msg-${m.id}`} className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
        isUser ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary"
      }`}>
        {isUser ? "You" : <Sparkles className="h-4 w-4" />}
      </div>
      <div className={`min-w-0 max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${isUser ? "bg-primary text-primary-foreground" : "bg-muted/40"}`}>
        {!isUser && <div className="mono mb-1 text-[10px] uppercase tracking-widest opacity-70">{managerName}</div>}
        <LeadReferenceRenderer text={m.content} isUser={isUser} onOpenLead={(id) => nav(`${window.location.pathname.startsWith("/admin") ? "/admin" : "/app"}/leads/${id}`)} />
        {m.streaming && <span className="ml-1 inline-block h-3 w-1 animate-pulse bg-current opacity-60" aria-label="typing" />}
        {!m.streaming && (
          <div className="mono mt-1 text-[9px] opacity-60">{fromNow(m.created_at)}</div>
        )}
      </div>
    </div>
  );
}
